/**
 * HeroSection — Phase E split (May 6, 2026).
 *
 * Hero block of `LandingPage` (headline + tagline + Start Design /
 * Book / My Projects buttons). Pure presentational; state and
 * handlers are owned by the parent.
 *
 * Markup, classes, data-testids, and conditional rendering of the
 * "My Projects" button (only when authenticated) are byte-identical
 * to the previous inline JSX.
 */
import { ArrowRight, FolderOpen, Calendar } from 'lucide-react';
import { Button } from '@/ui/button';

const HeroSection = ({
  isAuthenticated,
  onStartDesign,
  onBookCustomize,
  onOpenMyProjects,
}) => (
  <section className="py-12 sm:py-20 px-4 sm:px-8">
    <div className="max-w-4xl mx-auto text-center">
      <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
        Design Your Perfect
        <span className="block text-teal-400">Custom Closet</span>
      </h2>
      <p className="text-slate-400 text-base sm:text-lg lg:text-xl max-w-2xl mx-auto leading-relaxed mb-10">
        Create professional closet designs with precise measurements.
        No 3D required – just clean, accurate elevation views.
      </p>

      {/* Action buttons */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
        <Button
          onClick={onStartDesign}
          className="w-full sm:w-auto bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold px-8 py-6 text-lg rounded-xl glow-teal transition-all active:scale-95"
          data-testid="start-design-btn"
        >
          Start Design
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>

        <Button
          onClick={onBookCustomize}
          variant="outline"
          className="w-full sm:w-auto border-amber-500 hover:bg-amber-500/10 text-amber-400 px-8 py-6 text-lg rounded-xl transition-all"
          data-testid="book-customize-btn"
        >
          <Calendar className="mr-2 h-5 w-5" />
          Book
        </Button>

        {isAuthenticated && (
          <Button
            onClick={onOpenMyProjects}
            variant="outline"
            className="w-full sm:w-auto border-slate-600 hover:bg-slate-800 text-slate-300 px-8 py-6 text-lg rounded-xl transition-all"
            data-testid="my-projects-landing-btn"
          >
            <FolderOpen className="mr-2 h-5 w-5" />
            My Projects
          </Button>
        )}
      </div>
    </div>
  </section>
);

export default HeroSection;
